import { format, formatDistance, parseISO, type Locale } from "date-fns";
import { et } from "date-fns/locale";
import type { Language } from "@/lib/i18n/types";

/** Day-month-year — the date format used everywhere in the issue detail panel. */
export const PANEL_DATE_FORMAT = "dd MMM yyyy";

/** date-fns locale for the app language; English is date-fns' default. */
export function panelDateLocale(language: Language): Locale | undefined {
  return language === "et" ? et : undefined;
}

/** Absolute panel date, e.g. "16 Jun 2026". Falsy/invalid input renders "—". */
export function formatPanelDate(dateStr: string | null | undefined, locale?: Locale): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, PANEL_DATE_FORMAT, { locale });
}

/**
 * Relative comment timestamp (WHA-3810): "Just now" under a minute, a
 * date-fns relative phrase for earlier today, "Yesterday" for the previous
 * calendar day, and the absolute panel date beyond that.
 */
export interface CommentTimestampLabels {
  justNow: string;
  yesterday: string;
  locale?: Locale;
}

export function formatCommentTimestamp(
  dateStr: string,
  labels: CommentTimestampLabels,
  now: Date = new Date()
): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  if (now.getTime() - d.getTime() < 60_000) return labels.justNow;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d.getTime() >= startOfToday)
    return formatDistance(d, now, { addSuffix: true, locale: labels.locale });
  // Actual previous calendar day, not startOfToday - 24h: DST days are
  // 23h/25h long and a fixed offset mis-buckets the boundary hour.
  const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
  if (d.getTime() >= startOfYesterday) return labels.yesterday;
  return format(d, PANEL_DATE_FORMAT, { locale: labels.locale });
}

/** A due date strictly before today, on an issue that isn't done yet. */
export function isDueDateOverdue(
  dueDate: string | null | undefined,
  isDone: boolean,
  now: Date = new Date()
): boolean {
  if (!dueDate || isDone) return false;
  // parseISO, not new Date(): a date-only "YYYY-MM-DD" must parse as LOCAL
  // midnight — the native constructor parses it as UTC and flags issues
  // overdue a day early in negative-offset timezones.
  const due = parseISO(dueDate);
  if (Number.isNaN(due.getTime())) return false;
  return due < new Date(now.getFullYear(), now.getMonth(), now.getDate());
}
